import cx from 'classnames'
import React, { CSSProperties } from 'react'
import { useFormContext } from 'react-hook-form'

import { TRACKING_MAPPING } from '@js/common/constants'

import Icon from '@f/elements/Icon'

import { $TSFixMe } from '@f-fertighaus/applications/UserAccount/interfaces/utils'

import { FormErrorMessage } from './ErrorMessage'

interface FormCheckboxProps {
  label: string
  name: string
  className?: string
  theme?: string
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & {
    'data-sel': string
  }
  labelClassNames?: string
  description?: string
  descriptionClassNames?: string
  image?: {
    path: string
    alt: string
  }
  groupId?: string
  checkedIcon?: string

  wtData?: $TSFixMe

  disabled?: boolean
}

const useCheckbox = ({ name }: Pick<FormCheckboxProps, 'name'>) => {
  const { register, formState, getFieldState, watch } = useFormContext()
  const { isValidating } = formState
  const { error, isTouched } = getFieldState(name, formState)

  const checkboxValue = watch(name)

  const displayError = error?.message as string

  return {
    register,
    isValidating,
    isTouched,
    checkboxValue,
    displayError
  }
}

const clickHandlerFactory = ({
  name,
  wtData,
  label
}: Pick<FormCheckboxProps, 'name' | 'wtData' | 'label'>) => () => {
  window._wt({
    category: 'checkbox',
    action: 'click',
    values: [
      {
        type:
          TRACKING_MAPPING[name] && TRACKING_MAPPING[name].label
            ? TRACKING_MAPPING[name].label
            : name,
        value: wtData.valueOverride ? wtData.valueOverride : label
      }
    ],
    ...(wtData['checkbox'] ? wtData['checkbox'] : {})
  })
}

export const Checkbox = ({
  label,
  name,
  className,
  theme = 'form',
  inputProps,
  labelClassNames,
  description,
  descriptionClassNames,
  checkedIcon,
  wtData = {},
  disabled,
  ...props
}: FormCheckboxProps) => {
  const {
    register,
    isValidating,
    isTouched,
    checkboxValue,
    displayError
  } = useCheckbox({ name })

  const handleClick = clickHandlerFactory({ name, wtData, label })

  return (
    <>
      <label
        className={cx('FormCheckbox-wrapper', className, {
          'f-c-bo-war f-m-b-010': displayError,
          'f-m-b-020': !displayError && !className?.includes('f-m-b-'),
          'FormCheckbox--disabled': disabled,
          'is-validating': isValidating,
          'f-c-bo-suc400': isTouched && !displayError,
          'f-p-l-040': theme === 'form'
        })}
        {...props}>
        <input
          type='checkbox'
          className='FormCheckbox'
          onClick={() => handleClick()}
          {...register(name)}
          {...inputProps}
        />
        {
          <>
            {/form/.test(theme) && (
              <span className='[ f-pos-rel ] FormCheckbox-square'>
                {checkboxValue ? (
                  <Icon
                    icon='check-16px'
                    className={
                      '[ f-icon-030 f-c-st-whi ] FormCheckbox-square-check is-checked-icon ]'
                    }
                  />
                ) : (
                  <Icon
                    icon='check-16px'
                    className='[ f-icon-030 f-c-st-neu050 ] FormCheckbox-square-check unchecked'
                  />
                )}
              </span>
            )}

            {label && (
              <span
                className={cx(
                  `[ f-pos-rel ]`,
                  labelClassNames,
                  /image-label-/.test(theme) &&
                    checkboxValue &&
                    '[ f-c-ba-pri f-c-fo-whi ]',
                  /image-label-vendor/.test(theme) &&
                    '[ f-d-bl f-t-mini f-t-bold f-c-ba-whi f-c-fo-pri f-t-centre f-p-h-010 f-p-b-010 ] FormCheckbox-vendor-name'
                )}>
                {label}
              </span>
            )}
            {description && (
              <span
                className={cx(
                  `FormCheckbox-description ${
                    /image-label-/.test(theme) && checkboxValue
                      ? '[ f-c-ba-pri f-c-fo-whi ]'
                      : '[ f-c-fo-pri ]'
                  }`,
                  descriptionClassNames
                )}>
                {description}
              </span>
            )}

            {checkedIcon && checkboxValue && (
              <span className='[ f-c-ba-whi f-p-a-005 ] FormCheckbox-icon'>
                <Icon
                  icon={checkedIcon}
                  className='[ f-icon-020 f-c-st-ter f-d-bl ]'
                />
              </span>
            )}
          </>
        }
      </label>
      {displayError && <FormErrorMessage>{displayError}</FormErrorMessage>}
    </>
  )
}

interface FormCheckboxWithImageLabelProps
  extends Omit<FormCheckboxProps, 'label'> {
  label?: string
  image: {
    path: string
    alt: string
    flexLayout: boolean
    parameters?: {
      className: string
      style?: CSSProperties
    }
    viewBox?: string
    type?: string
  }
}

export const CheckboxWithImageLabel = ({
  label,
  name,
  className,
  theme = 'form',
  inputProps,
  labelClassNames,
  description,
  descriptionClassNames,
  checkedIcon,
  wtData = {},
  disabled,
  image,
  ...props
}: FormCheckboxWithImageLabelProps) => {
  const { register, isValidating, checkboxValue, displayError } = useCheckbox({
    name
  })

  const handleClick = clickHandlerFactory({ name, wtData, label })

  return (
    <>
      <label
        className={cx(
          'FormCheckbox-wrapper',
          theme && `FormCheckbox-wrapper-${theme}`,
          className,
          {
            'f-c-bo-war f-m-b-010': displayError,
            'f-m-b-020': !displayError && !className?.includes('f-m-b-'),
            'FormCheckbox--disabled': disabled,
            'is-validating': isValidating,
            'f-c-bo-suc400': checkboxValue,
            'f-p-l-040': theme === 'form'
          }
        )}
        {...props}>
        <input
          type='checkbox'
          className='FormCheckbox'
          onClick={() => handleClick()}
          {...register(name)}
          {...inputProps}
        />
        {
          <>
            {/form/.test(theme) && (
              <span className='[ f-pos-rel ] FormCheckbox-square'>
                {checkboxValue ? (
                  <Icon
                    icon='check-16px'
                    className={
                      '[ f-icon-030 f-c-st-whi ] FormCheckbox-square-check is-checked-icon ]'
                    }
                  />
                ) : (
                  <Icon
                    icon='check-16px'
                    className='[ f-icon-030 f-c-st-neu050 ] FormCheckbox-square-check unchecked'
                  />
                )}
              </span>
            )}
            {/image-label-/.test(theme) && image.path != null && (
              <span
                className={`${
                  /image-label-vendor/.test(theme)
                    ? '[ f-d-bl f-pos-rel ] Formcheckbox-image-wrapper'
                    : '[ f-d-bl f-pos-rel ] FormCheckbox-image'
                } `}>
                {/svg-symbol/.test(image.type) ? (
                  <svg
                    aria-hidden='true'
                    role='img'
                    focusable='false'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox={image.viewBox || '0 0 320 240'}
                    width={/image-label-small/.test(theme) ? 160 : 240}
                    height={/image-label-small/.test(theme) ? 120 : 180}
                    style={{
                      padding: /image-label-large/.test(theme) ? 24 : 0
                    }}>
                    <use xlinkHref={image.path} />
                  </svg>
                ) : image.flexLayout ? (
                  <img
                    alt='checkbox'
                    src={image.path}
                    className={`${
                      /image-label-vendor/.test(theme)
                        ? '[ f-d-bl f-radius-card-large f-p-t-010 ] Formcheckbox-vendor-image'
                        : '[ f-d-bl f-radius-card-large ]'
                    }`}
                  />
                ) : (
                  <img
                    alt='checkbox'
                    src={image.path}
                    width={160}
                    height={120}
                    className={cx(image.parameters?.className)}
                    style={image.parameters?.style}
                  />
                )}
              </span>
            )}
            {label && (
              <span
                className={cx(
                  '[ f-pos-rel ]',
                  /image-label-/.test(theme) &&
                    checkboxValue &&
                    '[ f-c-ba-pri f-c-fo-whi ]',
                  /image-label-vendor/.test(theme) &&
                    '[ f-d-bl f-t-mini f-t-bold f-c-ba-whi f-c-fo-pri f-t-centre f-p-h-010 f-p-b-010 ] FormCheckbox-vendor-name',
                  labelClassNames
                )}>
                {label}
              </span>
            )}
            {description && (
              <span
                className={cx(
                  `FormCheckbox-description ${
                    /image-label-/.test(theme) && checkboxValue
                      ? '[ f-c-ba-pri f-c-fo-whi ]'
                      : '[ f-c-fo-pri ]'
                  }`,
                  descriptionClassNames
                )}>
                {description}
              </span>
            )}

            {checkedIcon && checkboxValue && (
              <span className='[ f-c-ba-whi f-p-a-005 ] FormCheckbox-icon'>
                <Icon
                  icon={checkedIcon}
                  className='[ f-icon-020 f-c-st-ter f-d-bl ]'
                />
              </span>
            )}
          </>
        }
      </label>
      {displayError && <FormErrorMessage>{displayError}</FormErrorMessage>}
    </>
  )
}
